import type { Op } from 'quill/core';
import React, { useState } from 'react';
import { CREATION_ID } from '../../editor/editorSlice';
import { MentionBaseProvider } from '../../editor/mentions/MentionBase';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { Comment_CommentFragment } from '../../graphql/generated/graphql';
import MagneticWallSheet from '../MagneticWallSheet';
import useMagneticWallSheetContext from '../useMagneticWallSheetContext';
import Comment from './Comment';
import CommentCreate from './CommentCreate';
import styles from './CommentThreadSheet.module.scss';
import FoldedComments from './FoldedComments';
import useCommentActions from './useCommentActions';

export const CommentThreadSheetContext = ({
  id,
  type,
  highlight,
}: Parameters<typeof useMagneticWallSheetContext>[0]) => {
  const { highlighted, before, after } = useMagneticWallSheetContext({
    type,
    id,
    highlight,
  });

  return highlighted && highlighted.length > 0 ? (
    <MentionBaseProvider containerType="comment">
      <div className={styles.sheetContextContainer}>
        {before}
        {highlighted}
        {after}
      </div>
    </MentionBaseProvider>
  ) : null;
};

const POST_FRAGMENT = gql(`
  fragment CommentThreadSheet_post on RootPostFields {
    ...Comment_post
    ...CommentCreate_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment CommentThreadSheet_thread on PublicThreadFields {
    id
    comments {
      id
      ...Comment_comment
    }
    ...CommentCreate_thread
  }
`);

const FOCUSED_COMMENT_EXPAND_LIMIT = 6;

interface CommentThreadSheetProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  onClose: () => void;
  onInputChange: (content: Op[]) => void;
  unsavedInputValue?: Op[];
}

const CommentThreadSheet = ({
  post,
  onClose,
  thread,
  onInputChange,
  unsavedInputValue,
}: CommentThreadSheetProps) => {
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const { resolveThread } = useCommentActions(
    thread.id,
    post,
    thread.comments[0],
  );

  const isCreating = thread.id === CREATION_ID;

  return (
    <MagneticWallSheet
      onClose={onClose}
      title={isCreating ? 'New comment' : 'Comment'}
      headerContent={
        <CommentThreadSheetContext id={thread.id} type="comment" highlight />
      }
      inputRow={
        <CommentCreate
          post={post}
          thread={thread}
          onChange={onInputChange}
          initialContent={unsavedInputValue}
          disabled={editingCommentId !== null}
        />
      }
    >
      {!isCreating && (
        <>
          {thread.comments && thread.comments.length > 0 && (
            <FoldedComments<Comment_CommentFragment>
              comments={thread.comments}
              limit={FOCUSED_COMMENT_EXPAND_LIMIT}
              isExpanded
              renderComment={(comment, idx) => (
                <Comment
                  key={comment.id}
                  expanded
                  first={idx === 0}
                  last={idx === thread.comments.length - 1}
                  comment={comment}
                  threadId={thread.id}
                  onResize={() => {}}
                  post={post}
                  editing={editingCommentId === comment.id}
                  onEdit={() => setEditingCommentId(comment.id)}
                  onCancelEdit={() => setEditingCommentId(null)}
                  onResolve={resolveThread}
                />
              )}
            />
          )}
        </>
      )}
    </MagneticWallSheet>
  );
};

export default CommentThreadSheet;
